import React, {
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from "react";
import { createPortal } from "react-dom";
import styles from "./modal.module.scss";
import { useDomainContext } from "@hooks/useDomainContext";
import { getFinalFontFamily } from "@page-builder/utils";

export default function Modal({
    children,
    closeIcon,
    width,
    active,
    backdropClosable,
    onClose,
    modalStyles,
    bodyStyles,
    header,
    modalClassName,
    wrapperClassName,
    pageBuilderVariables,
    closeButtonClassName,
    overflow,
    activeCloseOnEscButton,
    fontFamilyAsProp,
}: {
    children: ReactNode;
    closeIcon: ReactNode;
    width?: string | number;
    active: boolean;
    backdropClosable?: boolean;
    onClose: () => void;
    modalStyles?: React.CSSProperties | undefined;
    bodyStyles?: React.CSSProperties | undefined;
    header?: string | undefined;
    modalClassName?: string;
    wrapperClassName?: string;
    closeButtonClassName?: string;
    overflow?: string;

    pageBuilderVariables?: React.CSSProperties | undefined;
    activeCloseOnEscButton?: boolean;
    fontFamilyAsProp?: string;
}): ReactElement {
    const { routeConfig, locale } = useDomainContext();
    const [mounted, setMounted] = useState(false);
    const [animation, setAnimation] = useState(false);

    const startAnimation = () => {
        setTimeout(() => {
            setAnimation(true);
        }, 100);
    };

    const resetAnimation = () => {
        setAnimation(false);
    };

    const handleEsc = useCallback((event: KeyboardEvent) => {
        if (event.key === "Escape") {
            onClose(); // close modal
        }
    }, []);

    useEffect(() => {
        if (mounted) {
            if (active) {
                if (activeCloseOnEscButton) {
                    document.addEventListener("keydown", handleEsc);
                }
                document
                    .getElementsByTagName("html")[0]
                    .classList.add("overflow-hidden");
                document.body.classList.add("overflow-hidden");
                if (window.innerWidth > 1024) {
                    document.body.style.width = "calc(100% - 15px)";
                }
                startAnimation();
            } else {
                if (activeCloseOnEscButton) {
                    document.removeEventListener("keydown", handleEsc);
                }
                document
                    .getElementsByTagName("html")[0]
                    .classList.remove("overflow-hidden");
                document.body.classList.remove("overflow-hidden");
                document.body.style.width = "";
                resetAnimation();
            }
        }
        return () => {
            if (activeCloseOnEscButton) {
                document.removeEventListener("keydown", handleEsc);
            }
            document
                .getElementsByTagName("html")[0]
                .classList.remove("overflow-hidden");
            document.body.classList.remove("overflow-hidden");
            document.body.style.width = "";
            resetAnimation();
        };
    }, [active]);

    useEffect(() => {
        setMounted(true);
    }, []);

    const getStyleObject = (): React.CSSProperties => {
        return {
            ...(pageBuilderVariables ? { ...pageBuilderVariables } : {}),
            ...(fontFamilyAsProp
                ? { fontFamily: fontFamilyAsProp }
                : routeConfig
                ? { fontFamily: getFinalFontFamily(routeConfig, locale) }
                : {}),
        };
    };

    if (mounted) {
        return createPortal(
            <div>
                <div
                    className={`${styles["wrapper"]} ${
                        active ? "flex" : "hidden"
                    } ${wrapperClassName ?? ""}`}
                    style={getStyleObject()}
                >
                    {active && (
                        <div
                            onClick={backdropClosable ? onClose : undefined}
                            className={styles["backdrop"]}
                        />
                    )}
                    <div
                        className={`${styles["modal"]} ${
                            wrapperClassName ? styles[wrapperClassName] : ""
                        } 
                        ${modalClassName ?? ""}
                        ${
                            overflow && overflow === "visible"
                                ? styles["overflow-visible"]
                                : ""
                        }
                        
                        duration-500 transition-all ease-in-out transform ${
                            animation
                                ? "-translate-y-0 opacity-100"
                                : "-translate-y-10 opacity-0"
                        }`}
                        style={{
                            width: width
                                ? typeof width === "string"
                                    ? `${width}px`
                                    : width
                                : "520px",
                            maxWidth: "calc(100vw - 32px)",
                            pointerEvents: "auto",

                            ...modalStyles,
                        }}
                    >
                        {header && (
                            <div className={styles["header"]}>
                                <h1>{header}</h1>
                            </div>
                        )}

                        <div
                            className={`${styles["body"]} `}
                            style={bodyStyles}
                        >
                            <button
                                onClick={onClose}
                                type="button"
                                aria-label="Close"
                                className={`${closeButtonClassName as string} ${
                                    styles["close-button"]
                                }`}
                            >
                                <span className="close-icon">{closeIcon}</span>
                            </button>
                            <div style={{ height: "100%" }}>{children}</div>
                        </div>
                    </div>
                </div>
            </div>,
            document.body,
        );
    }

    return <></>;
}
